import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signUp, clearError } from '../store/slices/authSlice'
import AuthBrand from '../components/(auth)/AuthBrand'

const Signup = () => {
  const [formData, setFormData] = useState({
    businessName: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [errors, setErrors] = useState({})
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isLoading, error } = useSelector((state) => state.auth)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
    // Clear Redux error when user starts typing
    if (error) {
      dispatch(clearError())
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }
    try {
      // Only send required fields to backend
      const { confirmPassword, ...signupData } = formData
      await dispatch(signUp(signupData)).unwrap()
      navigate('/signin')
    } catch (error) {
      console.error('Sign up failed:', error)
    }
  }

  return (
    <div style={styles.container}>
      <style>
        {`
          input::placeholder {
            color: #BFBFBF !important;
          }
        `}
      </style>
      <form style={styles.form} onSubmit={handleSubmit}>
        <AuthBrand />
        <h1 style={styles.title}>Sign up</h1>
        
        {error && (
          <div style={styles.globalError}>
            {error}
          </div>
        )}

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Business Name</label>
          <input
            type="text"
            name="businessName"
            value={formData.businessName}
            onChange={handleChange}
            placeholder="business name"
            style={errors.businessName ? { ...styles.input, borderColor: '#ef4444' } : styles.input}
            onFocus={(e) => {
              e.target.style.borderColor = '#2563eb'
            }}
            onBlur={(e) => {
              e.target.style.borderColor = errors.businessName ? '#ef4444' : '#e2e8f0'
            }}
          />
          {errors.businessName && <div style={styles.error}>{errors.businessName}</div>}
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="email address"
            style={errors.email ? { ...styles.input, borderColor: '#ef4444' } : styles.input}
            onFocus={(e) => {
              e.target.style.borderColor = '#2563eb'
            }}
            onBlur={(e) => {
              e.target.style.borderColor = errors.email ? '#ef4444' : '#e2e8f0'
            }}
          />
          {errors.email && <div style={styles.error}>{errors.email}</div>}
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Password</label>
          <div style={styles.inputContainer}>
            <input 
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="password"
              style={errors.password ? { ...styles.input, borderColor: '#ef4444' } : styles.input}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb'
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.password ? '#ef4444' : '#e2e8f0'
              }}
            />
            <span 
              style={styles.eyeIcon}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </span>
          </div>
          {errors.password && <div style={styles.error}>{errors.password}</div>}
        </div>

        <div style={styles.fieldGroup}>
          <label style={styles.label}>Confirm password</label>
          <div style={styles.inputContainer}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="confirm password"
              style={errors.confirmPassword ? { ...styles.input, borderColor: '#ef4444' } : styles.input}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb'
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.confirmPassword ? '#ef4444' : '#e2e8f0'
              }}
            />
            <span 
              style={styles.eyeIcon}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
            </span>
          </div>
          {errors.confirmPassword && <div style={styles.error}>{errors.confirmPassword}</div>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={styles.button}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = '#1d4ed8'
            }
          }}
          onMouseLeave={(e) => {
            if (!isLoading) {
              e.target.style.backgroundColor = '#2563eb'
            }
          }}
        >
          {isLoading ? 'Creating Account...' : 'Next'}
        </button>
      </form>
    </div>
  )
}

export default Signup

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    
  },
  form: {
    width: '200%',
    maxWidth: '600px',
    backgroundColor: '#ffffff',
    padding: '40px',
    borderRadius: '8px',
   
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    textAlign: 'center',
    marginBottom: '32px',
    margin: '0 0 24px 0'
  },
  fieldGroup: {
    marginBottom: '24px',
   
  },
  inputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  eyeIcon: {
    position: 'absolute',
    right: '12px',
    cursor: 'pointer',
    fontSize: '16px',
    userSelect: 'none'
  },
  label: {
    display: 'block',
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '9px'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #BFBFBF',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    color: '#1e293b',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    boxSizing: 'border-box'
  },
  error: {
    color: '#ef4444',
    fontSize: '12px',
    marginTop: '4px'
  },
  button: {
    width: '100%',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    marginTop: '8px'
  },
  globalError: {
    color: '#ef4444',
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '16px',
    padding: '8px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '4px'
  }
}